/**
 * Naming the platform's targeting languages in the interface language.
 *
 * The platform keys language targeting by its English display name, and that
 * name is what we store and send back — so it is translated for display only,
 * never on the way out. Mapping each name once to a BCP-47 code lets Intl name
 * it in whatever language the interface is in, so adding a fifth or a tenth
 * interface language costs nothing here. A name we don't know (the platform
 * adds one, or "Other") simply stays as it came.
 */
import { currentLocale } from './index';

const CODES: Record<string, string> = {
  Afrikaans: 'af', Albanian: 'sq', Amharic: 'am', Arabic: 'ar', Armenian: 'hy',
  Azerbaijani: 'az', Basque: 'eu', Belarusian: 'be', Bengali: 'bn', Bosnian: 'bs',
  Bulgarian: 'bg', Burmese: 'my', Catalan: 'ca', 'Chinese (simplified)': 'zh-Hans',
  'Chinese (traditional)': 'zh-Hant', Croatian: 'hr', Czech: 'cs', Danish: 'da',
  Dutch: 'nl', English: 'en', Estonian: 'et', Filipino: 'fil', Finnish: 'fi',
  French: 'fr', Galician: 'gl', Georgian: 'ka', German: 'de', Greek: 'el',
  Gujarati: 'gu', Hebrew: 'he', Hindi: 'hi', Hungarian: 'hu', Icelandic: 'is',
  Indonesian: 'id', Italian: 'it', Japanese: 'ja', Kannada: 'kn', Kazakh: 'kk',
  Khmer: 'km', Korean: 'ko', Lao: 'lo', Latvian: 'lv', Lithuanian: 'lt',
  Macedonian: 'mk', Malay: 'ms', Malayalam: 'ml', Maltese: 'mt', Marathi: 'mr',
  Mongolian: 'mn', Nepali: 'ne', Norwegian: 'no', Persian: 'fa', Polish: 'pl',
  Portuguese: 'pt', Punjabi: 'pa', Romanian: 'ro', Russian: 'ru', Serbian: 'sr',
  Sinhala: 'si', Slovak: 'sk', Slovenian: 'sl', Spanish: 'es', Swahili: 'sw',
  Swedish: 'sv', Tamil: 'ta', Telugu: 'te', Thai: 'th', Turkish: 'tr',
  Ukrainian: 'uk', Urdu: 'ur', Uzbek: 'uz', Vietnamese: 'vi', Welsh: 'cy',
  Zulu: 'zu',
};

export function languageLabel(name: string): string {
  const code = CODES[name];
  if (!code) return name;
  try {
    const label = new Intl.DisplayNames([currentLocale()], { type: 'language' }).of(code);
    if (!label || label === code) return name;
    // Ukrainian and Russian name languages in lower case; a list reads better capitalised.
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return name;  // no Intl data for this locale
  }
}
